// Pure adapters: the client's `MaterialQuality` (grounding + readability, the
// quality the `evaluateMaterial` port returns) → the two right-dock review
// panels' view-models (F65).
//
//   - Faktagranskning: citation coverage per claim kind, each cited claim
//     with the source quote that backs it, broken citations, the consulted
//     sources.
//   - Pedagogisk kvalitet: the readability axis, with the heaviest sections
//     named. Concept-map and item-quality are not on the client wire, so the
//     panel does not show them at all.
//
// Pure: total maps over a value, no I/O, no casts.

import type {
    ConsultedSource,
    FactCheckViewModel,
} from "../../components/FactCheckPanel/FactCheckPanel.js";
import type {
    HeavySection,
    PedagogicalQualityViewModel,
} from "../../components/PedagogicalQuality/PedagogicalQuality.js";
import {
    type MaterialQuality,
    readabilityVerdict,
    type SectionLix,
} from "../../components/QualitySummary/QualitySummary.js";

export const factCheckModel = (
    quality: MaterialQuality | null,
): FactCheckViewModel => {
    if (quality === null) return { type: "not_checked" };
    const g = quality.grounding;
    if (g.type === "no-sources") return { type: "not_checked" };
    return {
        type: "checked",
        coverage: g.support,
        backed: g.backing,
        sources: g.sourceTitles.map(
            (title, i): ConsultedSource => ({ id: `src-${i}`, title }),
        ),
    };
};

// How many heavy sections to name — enough to start, not a wall.
const MAX_HEAVY_SECTIONS = 3;

// The sections that need attention on their own (same bands the material-level
// verdict flags), heaviest first.
export const heaviestSections = (
    sections: readonly SectionLix[],
): readonly HeavySection[] =>
    // `filter` returns a fresh array, so the in-place sort touches no input.
    sections
        .filter((s) => readabilityVerdict(s.band) === "attention")
        .sort((a, b) => b.lix - a.lix)
        .slice(0, MAX_HEAVY_SECTIONS)
        .map((s) => ({ id: s.id, title: s.title }));

export const pedagogicalQualityModel = (
    quality: MaterialQuality | null,
): PedagogicalQualityViewModel => ({
    readability:
        quality === null || quality.readability.band === undefined
            ? { type: "not_yet_checked" }
            : {
                  type: "checked",
                  lix: quality.readability.lix,
                  band: quality.readability.band,
                  words: quality.readability.words,
                  sentences: quality.readability.sentences,
                  heaviestSections: heaviestSections(
                      quality.readability.sections,
                  ),
              },
});
